import { useState, useEffect, useCallback, useRef } from "react";
import { Home, Activity, Bike as BikeIcon, Pencil, Trash2, CalendarDays, ChevronDown, ChevronRight, MessageCircle, Droplet, Send, HelpCircle, Camera } from "lucide-react";

// ---------------------------------------------------------------
// Half Marathon Training Tracker — its own mini-app, mounted at
// /training the same way MealPlanner.js is mounted at /meal-planner
// (see src/index.js). Deliberately its own visual identity — light,
// energetic, a little playful — distinct from both the dark Command
// Center and Meal Planner's cork/gold cookbook look. Own useIsMobile,
// own fonts, own everything, matching how these mini-apps already
// don't share code with each other.
// ---------------------------------------------------------------

const WORKER_URL = "https://anthropic-proxy-worker.seancprince.workers.dev";
const RACE_DATE = "2026-11-14";
const RACE_NAME = "Rivers & Rails Half Marathon";
const RACE_LOCATION = "Brunswick, MD";

const COLOR = {
  bg: "#FAF9F6",
  card: "#FFFFFF",
  cardSoft: "#FFF3EF",
  ink: "#2B2A28",
  inkSoft: "#8A8681",
  coral: "#FF6B4A",
  coralDeep: "#E14E30",
  coralSoft: "#FFEDE7",
  teal: "#2DBFA6",
  tealSoft: "#E3F9F4",
  amber: "#F5A623",
  amberSoft: "#FFF4DE",
  border: "#EFEAE3",
  danger: "#E8543D",
};

const RUN_TYPES = [
  { id: "easy", label: "Easy" },
  { id: "tempo", label: "Tempo" },
  { id: "interval", label: "Interval" },
  { id: "long_run", label: "Long run" },
  { id: "benchmark", label: "Benchmark" },
  { id: "race", label: "Race" },
];

const NAV = [
  { id: "dashboard", label: "Today", icon: Home },
  { id: "log-run", label: "Run", icon: Activity },
  { id: "log-bike", label: "Bike", icon: BikeIcon },
  { id: "plan", label: "Plan", icon: CalendarDays },
  { id: "coach", label: "Coach", icon: MessageCircle },
  { id: "water", label: "Water", icon: Droplet },
];

// Pace-zone legend — the "general buckets" approach to pacing, anchored off
// the 8:30/mi goal race pace rather than a separate hand-maintained table.
// Ranges expressed in seconds/mile; mph is derived (60*60/sec), not stored,
// so it's always mathematically consistent with the pace shown next to it.
// The Week 3 benchmark run is meant to sanity-check/refine these over time.
const PACE_ZONES = {
  recovery: { label: "Recovery", range: [600, 630], desc: "Very easy, full-conversation pace. Used for warm-ups, cool-downs, and recovery jogs." },
  easy: { label: "Easy", range: [570, 600], desc: "Comfortable and conversational. Most of your weekly miles live here — including long runs." },
  goal: { label: "Goal / Race pace", range: [500, 520], desc: "Your target half-marathon pace (8:30/mi). Used late in long runs and race-pace segments." },
  tempo: { label: "Tempo", range: [480, 500], desc: "Comfortably hard — a few words at a time, not full sentences. Sometimes called “threshold” pace." },
  interval: { label: "Interval", range: [450, 475], desc: "Hard, controlled effort — roughly 5K race effort. Used for short repeats with jog recovery between." },
};
const STRIDES_ZONE = { label: "Strides", desc: "Quick, relaxed pickups toward a fast-but-controlled speed for 20 seconds. Not a sprint — think “fast and smooth,” not “all-out.”" };
const ALL_OUT_ZONE = { label: "All-out", desc: "The hardest pace you can sustain for the distance — treat it like racing." };

function paceZoneInfo(zone) {
  if (zone === "strides") return STRIDES_ZONE;
  if (zone === "all_out") return ALL_OUT_ZONE;
  return PACE_ZONES[zone] || null;
}
function paceRangeLabel(range) {
  if (!range) return null;
  const [fast, slow] = range;
  const mphFast = (3600 / fast).toFixed(1);
  const mphSlow = (3600 / slow).toFixed(1);
  return `${formatPace(fast)}–${formatPace(slow)}/mi · ${mphSlow}–${mphFast} mph`;
}

const GLOSSARY = [
  { term: "Strides", def: "Short (20-second) pickups to a fast-but-relaxed speed, done after an easy run to sharpen turnover. Not sprints — full recovery between each." },
  { term: "Tempo run", def: "A sustained “comfortably hard” effort, faster than easy pace but not all-out. Builds your ability to hold a strong pace for a long stretch." },
  { term: "Interval / repeats", def: "Short, hard efforts (like 400m or 800m) with a jog recovery between each, repeated several times. Builds speed and running economy." },
  { term: "Benchmark / time trial", def: "A short all-out effort used to measure current fitness. Its result recalibrates the pace zones above." },
  { term: "Goal / race pace", def: "The pace you're targeting for race day — 8:30/mile for Rivers & Rails." },
  { term: "Taper", def: "The final 1–2 weeks before the race, where mileage drops sharply so your legs are fresh on race day." },
  { term: "Incline sim", def: "Treadmill incline work that mimics outdoor hills, since the course isn't flat." },
  { term: "Shakeout run", def: "A very short, easy run 1–2 days before the race to stay loose without adding fatigue." },
];

// Ride-type guidance from the source plan — genuinely type-based (every
// "easy" ride gets the same targets), not day-specific, so it's shown as a
// legend on the Plan tab rather than duplicated as literal numbers on every
// bike row.
const BIKE_GUIDANCE = {
  bike_easy: { cadence: "80–90 RPM", resistance: "28–35", feel: "Conversational — could talk in full sentences the whole time" },
  bike_moderate: { cadence: "85–95 RPM flat / 65–75 climbs", resistance: "40–48 flat / 50–58 climbs", feel: "Comfortably hard — short phrases only" },
};

// Matches the source plan's own phase headers.
function phaseForWeek(n) {
  if (n <= 4) return "Base Building";
  if (n <= 9) return "Build";
  if (n <= 13) return "Peak";
  if (n <= 16) return "Taper";
  return "Race Week";
}

function useIsMobile() {
  const evaluate = () => {
    if (typeof window === "undefined") return false;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches;
    const narrowish = window.matchMedia("(max-width: 900px)").matches;
    return narrow || (narrowish && isTouchDevice);
  };
  const [isMobile, setIsMobile] = useState(evaluate);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const mq2 = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsMobile(evaluate());
    mq.addEventListener("change", onChange);
    mq2.addEventListener("change", onChange);
    return () => { mq.removeEventListener("change", onChange); mq2.removeEventListener("change", onChange); };
  }, []);
  return isMobile;
}

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayKey() { return toDateKey(new Date()); }

function formatPace(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
function formatDate(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function daysUntilRace() {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const race = new Date(RACE_DATE + "T00:00:00");
  return Math.round((race - now) / 86400000);
}
function weeksUntilRace() {
  const d = daysUntilRace();
  return d <= 0 ? 0 : Math.ceil(d / 7);
}

// Rolling windows (last N days ending today), not calendar-week-aligned —
// stays meaningful no matter which day of the week it is.
function withinDays(dateStr, days) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - (days - 1));
  const d = new Date(dateStr + "T12:00:00"); d.setHours(0, 0, 0, 0);
  return d >= cutoff && d <= today;
}

function runRollup(logs) {
  if (!logs.length) return { miles: 0, pace: null, count: 0 };
  const miles = logs.reduce((s, l) => s + l.distance_mi, 0);
  const totalSec = logs.reduce((s, l) => s + l.distance_mi * l.pace_seconds, 0);
  return { miles, pace: miles > 0 ? Math.round(totalSec / miles) : null, count: logs.length };
}
function bikeRollup(logs) {
  if (!logs.length) return { miles: 0, minutes: 0, mph: null, count: 0 };
  const miles = logs.reduce((s, l) => s + (l.distance_mi || 0), 0);
  const minutes = logs.reduce((s, l) => s + l.duration_minutes, 0);
  return { miles, minutes, mph: miles > 0 && minutes > 0 ? +(miles / (minutes / 60)).toFixed(1) : null, count: logs.length };
}

async function apiGet(path) {
  const res = await fetch(`${WORKER_URL}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed`);
  return res.json();
}
async function apiSend(path, method, body) {
  const res = await fetch(`${WORKER_URL}${path}`, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`${method} ${path} failed`);
  return res.json();
}
async function apiDelete(path) {
  const res = await fetch(`${WORKER_URL}${path}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`DELETE ${path} failed`);
}

// ---------------------------------------------------------------

export default function TrainingTracker() {
  const isMobile = useIsMobile();
  const [view, setView] = useState("dashboard");
  const [plan, setPlan] = useState([]);
  const [runs, setRuns] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [waters, setWaters] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, r, b, w] = await Promise.all([apiGet("/training-plan"), apiGet("/runs"), apiGet("/bike"), apiGet("/water")]);
        setPlan(p); setRuns(r); setBikes(b); setWaters(w);
      } catch (e) {
        setToast("Couldn't load training data — check your connection.");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const flash = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(null), 2600); }, []);

  const todaysPlan = plan.filter((p) => p.date === todayKey());

  async function addRun(entry) {
    const created = await apiSend("/runs", "POST", entry);
    setRuns((r) => [created, ...r]);
    flash("Run logged 🎉");
  }
  async function updateRun(id, entry) {
    const updated = await apiSend(`/runs/${id}`, "PUT", entry);
    setRuns((r) => r.map((x) => (x.id === id ? updated : x)));
    flash("Run updated");
  }
  async function deleteRun(id) {
    await apiDelete(`/runs/${id}`);
    setRuns((r) => r.filter((x) => x.id !== id));
  }
  async function addBike(entry) {
    const created = await apiSend("/bike", "POST", entry);
    setBikes((b) => [created, ...b]);
    flash("Ride logged 🎉");
  }
  async function updateBike(id, entry) {
    const updated = await apiSend(`/bike/${id}`, "PUT", entry);
    setBikes((b) => b.map((x) => (x.id === id ? updated : x)));
    flash("Ride updated");
  }
  async function deleteBike(id) {
    await apiDelete(`/bike/${id}`);
    setBikes((b) => b.filter((x) => x.id !== id));
  }
  async function addWater(amount_oz) {
    const created = await apiSend("/water", "POST", { date: todayKey(), amount_oz });
    setWaters((w) => [created, ...w]);
  }
  async function deleteWater(id) {
    await apiDelete(`/water/${id}`);
    setWaters((w) => w.filter((x) => x.id !== id));
  }

  return (
    <div style={{ display: "flex", height: "100vh", background: COLOR.bg, flexDirection: isMobile ? "column" : "row", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input[type=number]::-webkit-inner-spin-button { opacity: 1; }
        @keyframes wave { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        @keyframes spill-drop { 0% { opacity: 0; transform: translateY(0) scale(0.6); } 30% { opacity: 1; } 100% { opacity: 0; transform: translateY(22px) scale(0.9); } }
      `}</style>

      {!isMobile && (
        <aside style={{ width: 210, flexShrink: 0, background: COLOR.card, borderRight: `1px solid ${COLOR.border}`, padding: "24px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ marginBottom: 24, paddingLeft: 8 }}>
            <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 18, color: COLOR.ink }}>Half Marathon</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLOR.inkSoft, marginTop: 2 }}>{RACE_NAME}</div>
          </div>
          {NAV.map((n) => {
            const active = view === n.id; const Icon = n.icon;
            return (
              <button key={n.id} onClick={() => setView(n.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, border: "none", background: active ? COLOR.coralSoft : "transparent", color: active ? COLOR.coralDeep : COLOR.inkSoft, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: active ? 600 : 500, textAlign: "left" }}>
                <Icon size={18} /> {n.label}
              </button>
            );
          })}
        </aside>
      )}

      <main style={{ flex: 1, overflow: "auto", padding: isMobile ? "16px 14px 90px" : "28px 32px" }}>
        {!loaded ? (
          <div style={{ fontFamily: "'Inter', sans-serif", color: COLOR.inkSoft, textAlign: "center", padding: "60px 0" }}>Loading…</div>
        ) : view === "dashboard" ? (
          <Dashboard isMobile={isMobile} plan={plan} todaysPlan={todaysPlan} runs={runs} bikes={bikes} onGoLog={setView} />
        ) : view === "log-run" ? (
          <LogRun isMobile={isMobile} runs={runs} todaysPlan={todaysPlan} onAdd={addRun} onUpdate={updateRun} onDelete={deleteRun} />
        ) : view === "log-bike" ? (
          <LogBike isMobile={isMobile} bikes={bikes} onAdd={addBike} onUpdate={updateBike} onDelete={deleteBike} />
        ) : view === "plan" ? (
          <PlanView isMobile={isMobile} plan={plan} />
        ) : view === "coach" ? (
          <CoachView plan={plan} runs={runs} bikes={bikes} />
        ) : (
          <WaterView isMobile={isMobile} waters={waters} onAdd={addWater} onDelete={deleteWater} flash={flash} />
        )}
      </main>

      {isMobile && (
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", background: COLOR.card, borderTop: `1px solid ${COLOR.border}`, paddingBottom: "env(safe-area-inset-bottom, 0px)", zIndex: 50 }}>
          {NAV.map((n) => {
            const active = view === n.id; const Icon = n.icon;
            return (
              <button key={n.id} onClick={() => setView(n.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 4px", background: "transparent", border: "none", color: active ? COLOR.coral : COLOR.inkSoft }}>
                <Icon size={20} />
                <span style={{ fontSize: 10, fontFamily: "'Inter', sans-serif", fontWeight: active ? 600 : 500 }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: isMobile ? 84 : 24, right: isMobile ? 16 : 24, left: isMobile ? 16 : "auto", padding: "12px 18px", borderRadius: 14, background: COLOR.ink, color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 60 }}>{toast}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function Card({ children, style, accent }) {
  return (
    <div style={{ background: COLOR.card, borderRadius: 20, padding: 20, boxShadow: "0 2px 12px rgba(43,42,40,0.05)", borderLeft: accent ? `4px solid ${accent}` : undefined, ...style }}>
      {children}
    </div>
  );
}
function SectionLabel({ children }) {
  return <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 13, letterSpacing: "0.02em", color: COLOR.inkSoft, marginBottom: 10, textTransform: "uppercase" }}>{children}</div>;
}

function WorkoutTypeLabel(t) { return RUN_TYPES.find((r) => r.id === t)?.label || t || "Run"; }

function parseStructure(p) {
  if (!p.structure) return null;
  try { return JSON.parse(p.structure); } catch { return null; }
}

function PaceZoneChip({ zone, compact }) {
  const info = paceZoneInfo(zone);
  if (!info) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, color: COLOR.coralDeep, background: "#fff", padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {info.label}{!compact && info.range ? ` · ${paceRangeLabel(info.range)}` : ""}
    </span>
  );
}

// Renders a workout's structured segments as a compact table — used by both
// the Today card and the Plan tab so "today" is never a lesser version of
// what's on the full plan.
function SegmentTable({ segments }) {
  if (!segments || !segments.length) return null;
  return (
    <div style={{ marginTop: 8, borderRadius: 10, overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
      {segments.map((seg, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 10px", background: i % 2 ? "#fff" : "#FBFAF8", borderTop: i ? `1px solid ${COLOR.border}` : "none", flexWrap: "wrap" }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, fontWeight: 600, color: COLOR.ink }}>
            {seg.label}
            {seg.distance_mi != null && <span style={{ fontWeight: 400, color: COLOR.inkSoft }}> · {seg.distance_mi} mi</span>}
            {seg.duration_min != null && <span style={{ fontWeight: 400, color: COLOR.inkSoft }}> · {seg.duration_min} min</span>}
            {seg.recovery && <div style={{ fontWeight: 400, fontSize: 11, color: COLOR.inkSoft, marginTop: 1 }}>{seg.recovery}</div>}
          </div>
          <PaceZoneChip zone={seg.pace_zone} compact />
        </div>
      ))}
    </div>
  );
}

// Shared by AssignmentCard (Today) and PlanDayRow (Plan tab) so the plain-
// English instructions are identical in both places — never a simplified
// version on one and the full detail on the other.
function WorkoutDetail({ p }) {
  const segments = parseStructure(p);
  return (
    <>
      {p.plain_summary && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLOR.ink, lineHeight: 1.5, marginTop: 2 }}>{p.plain_summary}</div>}
      {segments ? (
        <SegmentTable segments={segments} />
      ) : p.pace_zone ? (
        <div style={{ marginTop: 6 }}><PaceZoneChip zone={p.pace_zone} /></div>
      ) : null}
      {p.incline_work && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, marginTop: 6 }}>⛰ Incline: {p.incline_work}</div>}
    </>
  );
}

function AssignmentCard({ p }) {
  const isRest = p.discipline === "rest";
  const isBike = p.discipline === "bike";
  const accent = isRest ? COLOR.inkSoft : isBike ? COLOR.teal : COLOR.coral;
  return (
    <div style={{ padding: "12px 14px", borderRadius: 14, background: isRest ? "#F5F4F1" : isBike ? COLOR.tealSoft : COLOR.coralSoft, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: accent, flexShrink: 0 }} />
        <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, color: COLOR.ink }}>{p.title}</span>
        {!p.required && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: COLOR.inkSoft, background: "#fff", padding: "1px 6px", borderRadius: 6 }}>optional</span>}
      </div>
      <WorkoutDetail p={p} />
    </div>
  );
}

function RollupRow({ label, run, bike }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${COLOR.border}` }}>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: COLOR.ink }}>{label}</div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, color: COLOR.inkSoft }}>
        <span style={{ color: COLOR.coralDeep, fontWeight: 600 }}>{run.miles.toFixed(2)} mi</span> · {formatPace(run.pace)}/mi
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, color: COLOR.inkSoft }}>
        <span style={{ color: COLOR.teal, fontWeight: 600 }}>{bike.miles.toFixed(1)} mi</span> · {bike.mph ? `${bike.mph} mph` : "—"}
      </div>
    </div>
  );
}

function Dashboard({ isMobile, todaysPlan, runs, bikes, onGoLog }) {
  const lastRun = runs[0];
  const lastBike = bikes[0];
  const days = daysUntilRace();
  const weeks = weeksUntilRace();

  const lastSessionRun = lastRun ? runRollup([lastRun]) : { miles: 0, pace: null, count: 0 };
  const week1Run = runRollup(runs.filter((r) => withinDays(r.date, 7)));
  const week2Run = runRollup(runs.filter((r) => withinDays(r.date, 14)));
  const lastSessionBike = lastBike ? bikeRollup([lastBike]) : { miles: 0, mph: null, count: 0 };
  const week1Bike = bikeRollup(bikes.filter((b) => withinDays(b.date, 7)));
  const week2Bike = bikeRollup(bikes.filter((b) => withinDays(b.date, 14)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>
      <Card style={{ background: `linear-gradient(135deg, ${COLOR.coral}, ${COLOR.coralDeep})`, color: "#fff" }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, opacity: 0.9, marginBottom: 4 }}>{RACE_NAME} · {RACE_LOCATION}</div>
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: isMobile ? 30 : 36, lineHeight: 1.1 }}>
          {days <= 0 ? "Race day! 🏁" : `${weeks} week${weeks === 1 ? "" : "s"} to go`}
        </div>
        {days > 0 && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, opacity: 0.9, marginTop: 4 }}>{days} days · Sat Nov 14, 7:00 AM</div>}
      </Card>

      <Card>
        <SectionLabel>Today · {formatDate(todayKey())}</SectionLabel>
        {todaysPlan.length === 0 ? (
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLOR.inkSoft }}>Nothing scheduled — plan not loaded for this date yet.</div>
        ) : (
          todaysPlan.map((p) => <AssignmentCard key={p.id} p={p} />)
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button onClick={() => onGoLog("log-run")} style={{ flex: 1, padding: "12px", borderRadius: 14, border: "none", background: COLOR.coral, color: "#fff", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Log Run</button>
          <button onClick={() => onGoLog("log-bike")} style={{ flex: 1, padding: "12px", borderRadius: 14, border: "none", background: COLOR.teal, color: "#fff", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Log Bike</button>
        </div>
      </Card>

      <Card>
        <SectionLabel>Rollups</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "0 0 8px", fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLOR.inkSoft, fontWeight: 600 }}>
          <div />
          <div>🏃 Run</div>
          <div>🚴 Bike</div>
        </div>
        <RollupRow label="Last session" run={lastSessionRun} bike={lastSessionBike} />
        <RollupRow label="Last 7 days" run={week1Run} bike={week1Bike} />
        <RollupRow label="Last 14 days" run={week2Run} bike={week2Bike} />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------

// Downscales a photo client-side before it ever leaves the phone — Apple
// Watch/Peloton screenshots are plenty legible well under full camera
// resolution, and keeping the upload small keeps Capture Activity fast on
// mobile data.
function resizeImageToBase64(file, maxDim = 1024, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Couldn't decode image"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality).split(",")[1]);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const RUN_CAPTURE_PROMPT = `Look at this fitness tracker screenshot (likely an Apple Watch workout summary or a running app). Extract, if legible: total distance in miles (convert from km by multiplying by 0.621371), total duration, and average pace per mile (convert from per-km by multiplying by 1.60934). Respond with ONLY a raw JSON object, no markdown, no explanation, in exactly this shape: {"distance_mi": number or null, "duration_min": integer or null, "duration_sec": integer or null, "pace_min": integer or null, "pace_sec": integer or null}. Use null for anything you can't read confidently — never guess.`;
const BIKE_CAPTURE_PROMPT = `Look at this Peloton (or similar indoor bike) screenshot. Extract, if legible: duration in minutes, average resistance, average cadence in RPM, and distance in miles (convert from km by multiplying by 0.621371). Respond with ONLY a raw JSON object, no markdown, no explanation, in exactly this shape: {"duration_minutes": integer or null, "avg_resistance": integer or null, "avg_cadence": integer or null, "distance_mi": number or null}. Use null for anything you can't read confidently — never guess.`;

function CaptureActivityButton({ kind, onExtract, accentColor }) {
  const inputRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | reading | error

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setStatus("reading");
    try {
      const base64 = await resizeImageToBase64(file);
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          max_tokens: 300,
          messages: [{
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: "image/jpeg", data: base64 } },
              { type: "text", text: kind === "run" ? RUN_CAPTURE_PROMPT : BIKE_CAPTURE_PROMPT },
            ],
          }],
        }),
      });
      const data = await res.json();
      const text = data?.content?.[0]?.text || "";
      const match = text.match(/\{[\s\S]*\}/);
      if (!match) throw new Error("No JSON in response");
      onExtract(JSON.parse(match[0]));
      setStatus("idle");
    } catch (err) {
      setStatus("error");
    }
  }

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: "none" }} />
      <button
        type="button" onClick={() => inputRef.current?.click()} disabled={status === "reading"}
        style={{ width: "100%", padding: "12px", borderRadius: 14, border: `1.5px dashed ${accentColor}`, background: "transparent", color: accentColor, fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 14, cursor: status === "reading" ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: status === "reading" ? 0.7 : 1 }}
      >
        <Camera size={18} />
        {status === "reading" ? "Reading photo…" : "Capture Activity"}
      </button>
      {status === "error" && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.danger, marginTop: 6, textAlign: "center" }}>Couldn't read that photo — try a clearer shot, or enter it manually below.</div>}
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, step, flex }) {
  return (
    <div style={{ flex: flex || 1 }}>
      {label && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLOR.inkSoft, marginBottom: 4 }}>{label}</div>}
      <input
        type="number" inputMode="decimal" step={step || "1"} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 15, color: COLOR.ink, outline: "none" }}
      />
    </div>
  );
}

// Splits a total-seconds value into zero-padded {min, sec} strings for the
// min/sec input pairs (pace and duration both use this shape).
function secToMinSecStrings(totalSec) {
  const clamped = Math.max(0, Math.round(totalSec));
  return { min: String(Math.floor(clamped / 60)), sec: String(clamped % 60).padStart(2, "0") };
}

function LogRun({ isMobile, runs, todaysPlan, onAdd, onUpdate, onDelete }) {
  const todaysRun = todaysPlan.find((p) => p.discipline === "run");
  const blank = { date: todayKey(), distance_mi: "", pace_min: "", pace_sec: "", duration_min: "", duration_sec: "", lastEdited: null, run_type: todaysRun?.workout_type || "easy", notes: "" };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  function startEdit(r) {
    setEditingId(r.id);
    const durSec = r.distance_mi > 0 ? Math.round(r.pace_seconds * r.distance_mi) : null;
    setForm({
      date: r.date, distance_mi: String(r.distance_mi),
      pace_min: String(Math.floor(r.pace_seconds / 60)), pace_sec: String(r.pace_seconds % 60).padStart(2, "0"),
      ...(durSec != null ? { duration_min: secToMinSecStrings(durSec).min, duration_sec: secToMinSecStrings(durSec).sec } : { duration_min: "", duration_sec: "" }),
      lastEdited: "pace", run_type: r.run_type, notes: r.notes || "",
    });
  }
  function cancelEdit() { setEditingId(null); setForm(blank); }

  // Distance, pace, and duration are mutually derivable (pace = duration ÷
  // distance). Whichever of pace/duration the runner typed into most
  // recently is treated as the source of truth; the other one — and, when
  // distance itself changes, whichever wasn't just typed — gets recomputed
  // from it. This is what lets either pace OR duration drive the other.
  function updateDistance(value) {
    setForm((f) => {
      const next = { ...f, distance_mi: value };
      const distance = parseFloat(value);
      if (!(distance > 0)) return next;
      if (next.lastEdited === "duration" && (next.duration_min || next.duration_sec)) {
        const totalDurationSec = (parseInt(next.duration_min || "0", 10) * 60) + parseInt(next.duration_sec || "0", 10);
        const { min, sec } = secToMinSecStrings(totalDurationSec / distance);
        next.pace_min = min; next.pace_sec = sec;
      } else if (next.pace_min || next.pace_sec) {
        const totalPaceSec = (parseInt(next.pace_min || "0", 10) * 60) + parseInt(next.pace_sec || "0", 10);
        const { min, sec } = secToMinSecStrings(totalPaceSec * distance);
        next.duration_min = min; next.duration_sec = sec;
      }
      return next;
    });
  }
  function updatePace(field, value) {
    setForm((f) => {
      const next = { ...f, [field]: value, lastEdited: "pace" };
      const distance = parseFloat(next.distance_mi);
      if (distance > 0) {
        const totalPaceSec = (parseInt(next.pace_min || "0", 10) * 60) + parseInt(next.pace_sec || "0", 10);
        const { min, sec } = secToMinSecStrings(totalPaceSec * distance);
        next.duration_min = min; next.duration_sec = sec;
      }
      return next;
    });
  }
  function updateDuration(field, value) {
    setForm((f) => {
      const next = { ...f, [field]: value, lastEdited: "duration" };
      const distance = parseFloat(next.distance_mi);
      if (distance > 0) {
        const totalDurationSec = (parseInt(next.duration_min || "0", 10) * 60) + parseInt(next.duration_sec || "0", 10);
        const { min, sec } = secToMinSecStrings(totalDurationSec / distance);
        next.pace_min = min; next.pace_sec = sec;
      }
      return next;
    });
  }

  // Merges whatever Capture Activity could read off a photo into the form,
  // then reconciles pace/duration the same way manual typing would.
  function handleCaptureExtract(parsed) {
    setForm((f) => {
      let next = { ...f };
      if (parsed.distance_mi != null) next.distance_mi = String(parsed.distance_mi);
      if (parsed.duration_min != null || parsed.duration_sec != null) {
        if (parsed.duration_min != null) next.duration_min = String(parsed.duration_min);
        if (parsed.duration_sec != null) next.duration_sec = String(parsed.duration_sec).padStart(2, "0");
        next.lastEdited = "duration";
      } else if (parsed.pace_min != null || parsed.pace_sec != null) {
        if (parsed.pace_min != null) next.pace_min = String(parsed.pace_min);
        if (parsed.pace_sec != null) next.pace_sec = String(parsed.pace_sec).padStart(2, "0");
        next.lastEdited = "pace";
      }
      const distance = parseFloat(next.distance_mi);
      if (distance > 0) {
        if (next.lastEdited === "duration" && (next.duration_min || next.duration_sec)) {
          const totalDurationSec = (parseInt(next.duration_min || "0", 10) * 60) + parseInt(next.duration_sec || "0", 10);
          const { min, sec } = secToMinSecStrings(totalDurationSec / distance);
          next.pace_min = min; next.pace_sec = sec;
        } else if (next.lastEdited === "pace" && (next.pace_min || next.pace_sec)) {
          const totalPaceSec = (parseInt(next.pace_min || "0", 10) * 60) + parseInt(next.pace_sec || "0", 10);
          const { min, sec } = secToMinSecStrings(totalPaceSec * distance);
          next.duration_min = min; next.duration_sec = sec;
        }
      }
      return next;
    });
  }

  async function submit() {
    const distance_mi = parseFloat(form.distance_mi);
    const min = parseInt(form.pace_min || "0", 10);
    const sec = parseInt(form.pace_sec || "0", 10);
    if (!distance_mi || (!min && !sec)) return;
    const entry = { date: form.date, distance_mi, pace_seconds: min * 60 + sec, run_type: form.run_type, notes: form.notes };
    if (editingId) await onUpdate(editingId, entry);
    else await onAdd(entry);
    cancelEdit();
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <SectionLabel>{editingId ? "Edit run" : "Log a run"}</SectionLabel>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CaptureActivityButton kind="run" accentColor={COLOR.coral} onExtract={handleCaptureExtract} />
          <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 15, color: COLOR.ink }} />
          <NumberField label="Distance (mi)" value={form.distance_mi} onChange={updateDistance} placeholder="8.47" step="0.01" />
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <NumberField label="Pace — min" value={form.pace_min} onChange={(v) => updatePace("pace_min", v)} placeholder="8" />
            <div style={{ fontSize: 22, color: COLOR.inkSoft, paddingBottom: 12 }}>:</div>
            <NumberField label="sec" value={form.pace_sec} onChange={(v) => updatePace("pace_sec", v)} placeholder="30" />
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, paddingBottom: 12, whiteSpace: "nowrap" }}>/mi</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <NumberField label="Duration — min" value={form.duration_min} onChange={(v) => updateDuration("duration_min", v)} placeholder="72" />
            <div style={{ fontSize: 22, color: COLOR.inkSoft, paddingBottom: 12 }}>:</div>
            <NumberField label="sec" value={form.duration_sec} onChange={(v) => updateDuration("duration_sec", v)} placeholder="15" />
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, paddingBottom: 12, whiteSpace: "nowrap" }}>total</div>
          </div>
          <select value={form.run_type} onChange={(e) => setForm((f) => ({ ...f, run_type: e.target.value }))} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 15, color: COLOR.ink }}>
            {RUN_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes — how'd it feel?" rows={2} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 14, color: COLOR.ink, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={submit} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "none", background: COLOR.coral, color: "#fff", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{editingId ? "Save" : "Log run"}</button>
            {editingId && <button onClick={cancelEdit} style={{ padding: "13px 18px", borderRadius: 14, border: `1px solid ${COLOR.border}`, background: "transparent", color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif", fontSize: 14, cursor: "pointer" }}>Cancel</button>}
          </div>
        </div>
      </Card>

      <SectionLabel>History</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {runs.length === 0 && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLOR.inkSoft }}>No runs logged yet.</div>}
        {runs.map((r) => (
          <Card key={r.id} style={{ padding: 14 }} accent={r.run_type === "race" ? COLOR.amber : COLOR.coral}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, color: COLOR.ink }}>{r.distance_mi} mi · {formatPace(r.pace_seconds)}/mi <span style={{ color: COLOR.inkSoft, fontWeight: 400 }}>· {WorkoutTypeLabel(r.run_type)}</span></div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, marginTop: 2 }}>{formatDate(r.date)}{r.notes ? ` — ${r.notes}` : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => startEdit(r)} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLOR.inkSoft, padding: isMobile ? 6 : 4 }}><Pencil size={16} /></button>
                <button onClick={() => onDelete(r.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLOR.danger, padding: isMobile ? 6 : 4 }}><Trash2 size={16} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function LogBike({ isMobile, bikes, onAdd, onUpdate, onDelete }) {
  const blank = { date: todayKey(), duration_minutes: "", avg_resistance: "", avg_cadence: "", distance_mi: "", notes: "" };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  function startEdit(b) {
    setEditingId(b.id);
    setForm({ date: b.date, duration_minutes: String(b.duration_minutes), avg_resistance: b.avg_resistance != null ? String(b.avg_resistance) : "", avg_cadence: b.avg_cadence != null ? String(b.avg_cadence) : "", distance_mi: b.distance_mi != null ? String(b.distance_mi) : "", notes: b.notes || "" });
  }
  function cancelEdit() { setEditingId(null); setForm(blank); }

  function handleCaptureExtract(parsed) {
    setForm((f) => ({
      ...f,
      duration_minutes: parsed.duration_minutes != null ? String(parsed.duration_minutes) : f.duration_minutes,
      avg_resistance: parsed.avg_resistance != null ? String(parsed.avg_resistance) : f.avg_resistance,
      avg_cadence: parsed.avg_cadence != null ? String(parsed.avg_cadence) : f.avg_cadence,
      distance_mi: parsed.distance_mi != null ? String(parsed.distance_mi) : f.distance_mi,
    }));
  }

  async function submit() {
    const duration_minutes = parseInt(form.duration_minutes, 10);
    if (!duration_minutes) return;
    const entry = {
      date: form.date, duration_minutes,
      avg_resistance: form.avg_resistance ? parseInt(form.avg_resistance, 10) : null,
      avg_cadence: form.avg_cadence ? parseInt(form.avg_cadence, 10) : null,
      distance_mi: form.distance_mi ? parseFloat(form.distance_mi) : null,
      notes: form.notes,
    };
    if (editingId) await onUpdate(editingId, entry);
    else await onAdd(entry);
    cancelEdit();
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <SectionLabel>{editingId ? "Edit ride" : "Log a ride"}</SectionLabel>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CaptureActivityButton kind="bike" accentColor={COLOR.teal} onExtract={handleCaptureExtract} />
          <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 15, color: COLOR.ink }} />
          <NumberField label="Duration (min)" value={form.duration_minutes} onChange={(v) => setForm((f) => ({ ...f, duration_minutes: v }))} placeholder="30" />
          <div style={{ display: "flex", gap: 10 }}>
            <NumberField label="Avg resistance" value={form.avg_resistance} onChange={(v) => setForm((f) => ({ ...f, avg_resistance: v }))} placeholder="42" />
            <NumberField label="Avg cadence (RPM)" value={form.avg_cadence} onChange={(v) => setForm((f) => ({ ...f, avg_cadence: v }))} placeholder="85" />
          </div>
          <NumberField label="Distance (mi, optional)" value={form.distance_mi} onChange={(v) => setForm((f) => ({ ...f, distance_mi: v }))} placeholder="10.2" step="0.1" />
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={2} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 14, color: COLOR.ink, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={submit} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "none", background: COLOR.teal, color: "#fff", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{editingId ? "Save" : "Log ride"}</button>
            {editingId && <button onClick={cancelEdit} style={{ padding: "13px 18px", borderRadius: 14, border: `1px solid ${COLOR.border}`, background: "transparent", color: COLOR.inkSoft, fontFamily: "'Inter', sans-serif", fontSize: 14, cursor: "pointer" }}>Cancel</button>}
          </div>
        </div>
      </Card>

      <SectionLabel>History</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {bikes.length === 0 && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLOR.inkSoft }}>No rides logged yet.</div>}
        {bikes.map((b) => (
          <Card key={b.id} style={{ padding: 14 }} accent={COLOR.teal}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, color: COLOR.ink }}>
                  {b.duration_minutes} min{b.distance_mi ? ` · ${b.distance_mi} mi` : ""}{b.avg_resistance ? ` · R${b.avg_resistance}` : ""}{b.avg_cadence ? ` · ${b.avg_cadence} RPM` : ""}
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, marginTop: 2 }}>{formatDate(b.date)}{b.notes ? ` — ${b.notes}` : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => startEdit(b)} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLOR.inkSoft, padding: isMobile ? 6 : 4 }}><Pencil size={16} /></button>
                <button onClick={() => onDelete(b.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLOR.danger, padding: isMobile ? 6 : 4 }}><Trash2 size={16} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function DisciplineBadge({ discipline }) {
  const map = {
    run: { label: "Run", bg: COLOR.coralSoft, fg: COLOR.coralDeep },
    bike: { label: "Bike", bg: COLOR.tealSoft, fg: COLOR.teal },
    rest: { label: "Rest", bg: "#F0EFEC", fg: COLOR.inkSoft },
  };
  const m = map[discipline] || map.rest;
  return <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: m.bg, color: m.fg, textTransform: "uppercase", letterSpacing: "0.03em", flexShrink: 0 }}>{m.label}</span>;
}

function PlanDayRow({ p }) {
  const guidance = p.discipline === "bike" ? BIKE_GUIDANCE[p.workout_type] : null;
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${COLOR.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: COLOR.inkSoft, minWidth: 78 }}>{formatDate(p.date)}</span>
        <DisciplineBadge discipline={p.discipline} />
        {!p.required && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: COLOR.inkSoft, fontStyle: "italic" }}>optional</span>}
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, fontWeight: 600, color: COLOR.ink }}>{p.title}</span>
        {p.distance_mi != null && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft }}>· {p.distance_mi} mi</span>}
      </div>
      <div style={{ marginLeft: 86 }}>
        <WorkoutDetail p={p} />
        {guidance && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.teal, marginTop: 4 }}>{guidance.cadence} · resistance {guidance.resistance}</div>}
      </div>
    </div>
  );
}

function GlossaryCard() {
  const [open, setOpen] = useState(false);
  return (
    <Card style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
        {open ? <ChevronDown size={16} color={COLOR.inkSoft} /> : <ChevronRight size={16} color={COLOR.inkSoft} />}
        <HelpCircle size={16} color={COLOR.inkSoft} />
        <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 13, color: COLOR.ink }}>What do these terms mean?</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          {GLOSSARY.map((g) => (
            <div key={g.term} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, color: COLOR.inkSoft, lineHeight: 1.5 }}>
              <strong style={{ color: COLOR.ink }}>{g.term}:</strong> {g.def}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PlanView({ isMobile, plan }) {
  const weeks = {};
  for (const p of plan) {
    if (!weeks[p.week_number]) weeks[p.week_number] = [];
    weeks[p.week_number].push(p);
  }
  const weekNumbers = Object.keys(weeks).map(Number).sort((a, b) => a - b);

  const today = todayKey();
  const currentWeek =
    plan.find((p) => p.date === today)?.week_number ??
    weekNumbers.find((w) => weeks[w].some((p) => p.date >= today)) ??
    weekNumbers[weekNumbers.length - 1];

  const [expanded, setExpanded] = useState(() => new Set(currentWeek ? [currentWeek] : []));
  function toggle(w) { setExpanded((prev) => { const next = new Set(prev); next.has(w) ? next.delete(w) : next.add(w); return next; }); }
  function expandAll() { setExpanded(new Set(weekNumbers)); }
  function collapseAll() { setExpanded(new Set()); }

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <SectionLabel>Full 17-week plan</SectionLabel>
        <div style={{ display: "flex", gap: 14 }}>
          <button onClick={expandAll} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: COLOR.coralDeep, background: "transparent", border: "none", cursor: "pointer", padding: isMobile ? "6px 0" : 0 }}>Expand all</button>
          <button onClick={collapseAll} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: COLOR.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: isMobile ? "6px 0" : 0 }}>Collapse all</button>
        </div>
      </div>

      <Card style={{ marginBottom: 12, background: COLOR.coralSoft }}>
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 12, color: COLOR.ink, marginBottom: 8 }}>Pace zones</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {Object.entries(PACE_ZONES).map(([id, z]) => (
            <div key={id} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft }}>
              <strong style={{ color: COLOR.ink }}>{z.label}</strong> — {paceRangeLabel(z.range)} — {z.desc}
            </div>
          ))}
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft }}><strong style={{ color: COLOR.ink }}>{STRIDES_ZONE.label}</strong> — {STRIDES_ZONE.desc}</div>
        </div>
      </Card>

      <Card style={{ marginBottom: 12, background: COLOR.tealSoft }}>
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 12, color: COLOR.ink, marginBottom: 6 }}>Peloton ride guidance</div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, marginBottom: 3 }}><strong style={{ color: COLOR.ink }}>Easy:</strong> {BIKE_GUIDANCE.bike_easy.cadence} · resistance {BIKE_GUIDANCE.bike_easy.resistance} — {BIKE_GUIDANCE.bike_easy.feel}</div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft }}><strong style={{ color: COLOR.ink }}>Moderate:</strong> {BIKE_GUIDANCE.bike_moderate.cadence} · resistance {BIKE_GUIDANCE.bike_moderate.resistance} — {BIKE_GUIDANCE.bike_moderate.feel}</div>
      </Card>

      <GlossaryCard />

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        {weekNumbers.map((w) => {
          const days = weeks[w].slice().sort((a, b) => (a.date < b.date ? -1 : 1));
          const isOpen = expanded.has(w);
          const isCurrent = w === currentWeek;
          const range = `${formatDate(days[0].date)} – ${formatDate(days[days.length - 1].date)}`;
          return (
            <Card key={w} style={{ padding: 0, overflow: "hidden" }} accent={isCurrent ? COLOR.coral : COLOR.border}>
              <button onClick={() => toggle(w)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: isCurrent ? COLOR.coralSoft : "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                {isOpen ? <ChevronDown size={16} color={COLOR.inkSoft} style={{ flexShrink: 0 }} /> : <ChevronRight size={16} color={COLOR.inkSoft} style={{ flexShrink: 0 }} />}
                <div>
                  <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 14, color: COLOR.ink }}>Week {w} · {phaseForWeek(w)}{isCurrent ? " · This week" : ""}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLOR.inkSoft }}>{range}</div>
                </div>
              </button>
              {isOpen && (
                <div style={{ padding: "0 16px 12px" }}>
                  {days.map((p) => <PlanDayRow key={p.id} p={p} />)}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// AI Coach — reuses the bare Anthropic-proxy POST pattern already used by
// AiBriefing/GoalsCoachPanel and Meal Planner's AskAI: no dedicated server
// route, prompt built client-side from data already in state, fresh call
// on every message (no caching).

function buildCoachSystemPrompt(plan, runs, bikes) {
  const today = todayKey();
  const days = daysUntilRace();
  const todayRows = plan.filter((p) => p.date === today);
  const upcoming = plan.filter((p) => p.date >= today).slice(0, 7);
  const recentRuns = runs.filter((r) => withinDays(r.date, 14));
  const recentBikes = bikes.filter((b) => withinDays(b.date, 14));
  const runRoll = runRollup(recentRuns);
  const bikeRoll = bikeRollup(recentBikes);

  const todayLine = todayRows.length
    ? todayRows.map((p) => `${p.discipline}${p.workout_type ? "/" + p.workout_type : ""}: ${p.plain_summary || p.title}`).join(" | ")
    : "No plan entry found for today.";
  const planLines = upcoming.map((p) => `${p.date} (${p.discipline}${p.workout_type ? "/" + p.workout_type : ""}): ${p.plain_summary || p.title}`).join("\n") || "No upcoming plan entries.";
  const runLines = recentRuns.map((r) => `${r.date}: ${r.distance_mi} mi @ ${formatPace(r.pace_seconds)}/mi (${WorkoutTypeLabel(r.run_type)})${r.notes ? " — " + r.notes : ""}`).join("\n") || "None logged in the last 14 days.";
  const bikeLines = recentBikes.map((b) => `${b.date}: ${b.duration_minutes} min${b.distance_mi ? ", " + b.distance_mi + " mi" : ""}${b.avg_resistance ? `, resistance ${b.avg_resistance}` : ""}${b.notes ? " — " + b.notes : ""}`).join("\n") || "None logged in the last 14 days.";
  const zoneLines = Object.values(PACE_ZONES).map((z) => `${z.label} ${paceRangeLabel(z.range)}`).join(", ");

  return `You are a supportive, knowledgeable running coach helping Sean train for the ${RACE_NAME} in ${RACE_LOCATION} on ${formatDate(RACE_DATE)} (${days} days away). Goal pace: 8:30/mile.

Pace zones: ${zoneLines}.

Today (${today}): ${todayLine}

Next 7 days on the plan:
${planLines}

Runs logged in the last 14 days (${runRoll.miles.toFixed(1)} mi total, avg pace ${formatPace(runRoll.pace)}/mi):
${runLines}

Rides logged in the last 14 days (${bikeRoll.miles.toFixed(1)} mi, ${bikeRoll.minutes} min total):
${bikeLines}

Answer Sean's questions using this context. Be encouraging but honest — flag it if he's falling behind the plan, pushing intensity too hard, or at injury risk (he has a history of IT band issues, though strength/prehab work is tracked separately outside this app). Keep answers concise and practical, plain English, no unexplained jargon. Don't invent data points that weren't given above. Reply in plain text only — no markdown (no asterisks, no headers, no bullet-point dashes), since your reply is displayed as plain text, not rendered markdown. Use line breaks for readability instead.`;
}

function CoachView({ plan, runs, bikes }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I can see your training plan and your logged runs and rides. Ask me anything — how today's workout should feel, whether you're on track, how to adjust if you're sore, race-day strategy, whatever's on your mind." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    try {
      const system = buildCoachSystemPrompt(plan, runs, bikes);
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ max_tokens: 1024, system, messages: nextMessages.map((m) => ({ role: m.role, content: m.content })) }),
      });
      const data = await res.json();
      const reply = data?.content?.[0]?.text || "Sorry, I couldn't generate a response — try again.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Something went wrong reaching the coach — check your connection and try again." }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      <SectionLabel>Coach</SectionLabel>
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 10, paddingBottom: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
            <div style={{
              padding: "10px 14px", borderRadius: 16,
              background: m.role === "user" ? COLOR.coral : COLOR.card,
              color: m.role === "user" ? "#fff" : COLOR.ink,
              boxShadow: m.role === "user" ? "none" : "0 2px 8px rgba(43,42,40,0.06)",
              fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap",
            }}>{m.content}</div>
          </div>
        ))}
        {sending && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: COLOR.inkSoft, paddingLeft: 4 }}>Coach is thinking…</div>}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: "flex", gap: 8, paddingTop: 8, borderTop: `1px solid ${COLOR.border}` }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask your coach anything…"
          style={{ flex: 1, padding: "12px 14px", borderRadius: 14, border: `1px solid ${COLOR.border}`, background: COLOR.bg, fontFamily: "'Inter', sans-serif", fontSize: 14, color: COLOR.ink, outline: "none" }}
        />
        <button onClick={send} disabled={sending || !input.trim()} style={{ width: 46, height: 46, borderRadius: 14, border: "none", background: COLOR.coral, color: "#fff", cursor: sending ? "default" : "pointer", opacity: sending || !input.trim() ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Water Tracker

const WATER_GOAL_OZ = 100;
const WATER_QUICK_ADDS = [8, 12, 17, 20, 32];

function WaterCup({ pct }) {
  const clamped = Math.min(pct, 1);
  const fillHeight = 90 * clamped;
  const fillY = 130 - fillHeight;
  const overflowing = pct >= 1;
  return (
    <svg viewBox="0 0 120 150" width="140" height="175" style={{ overflow: "visible" }}>
      <clipPath id="cupClip"><path d="M20 20 L100 20 L88 130 L32 130 Z" /></clipPath>
      <g clipPath="url(#cupClip)">
        <rect x="10" y={fillY} width="100" height={fillHeight + 20} fill={COLOR.teal} opacity="0.85" style={{ transition: "y 0.5s ease, height 0.5s ease" }} />
        <ellipse cx="60" cy={fillY} rx="46" ry="6" fill={COLOR.tealSoft} opacity="0.9" style={{ transition: "cy 0.5s ease", animation: "wave 1.8s ease-in-out infinite" }} />
      </g>
      <path d="M20 20 L100 20 L88 130 L32 130 Z" fill="none" stroke={COLOR.teal} strokeWidth="3" strokeLinejoin="round" />
      {overflowing && [0, 1, 2].map((i) => (
        <circle key={i} cx={45 + i * 15} cy="18" r="3.5" fill={COLOR.teal} style={{ animation: `spill-drop 1.1s ease-in-out ${i * 0.25}s infinite` }} />
      ))}
    </svg>
  );
}

function WaterView({ isMobile, waters, onAdd, onDelete, flash }) {
  const [busy, setBusy] = useState(false);
  const today = todayKey();
  const todays = waters.filter((w) => w.date === today);
  const total = todays.reduce((s, w) => s + w.amount_oz, 0);
  const pct = total / WATER_GOAL_OZ;

  async function quickAdd(oz) {
    setBusy(true);
    try {
      const wasUnder = total < WATER_GOAL_OZ;
      await onAdd(oz);
      if (wasUnder && total + oz >= WATER_GOAL_OZ) flash("Goal hit! 💧");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <SectionLabel>Water</SectionLabel>
      <Card style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 16, background: `linear-gradient(180deg, ${COLOR.tealSoft}, ${COLOR.card})` }}>
        <WaterCup pct={pct} />
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 28, color: COLOR.teal }}>{total} <span style={{ fontSize: 15, fontWeight: 500, color: COLOR.inkSoft }}>/ {WATER_GOAL_OZ} oz</span></div>
        {pct >= 1 && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: COLOR.teal }}>Goal hit — nice work! 💧</div>}
      </Card>

      <SectionLabel>Quick add</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
        {WATER_QUICK_ADDS.map((oz) => (
          <button key={oz} disabled={busy} onClick={() => quickAdd(oz)} style={{ padding: "16px 8px", borderRadius: 14, border: "none", background: COLOR.teal, color: "#fff", fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 16, cursor: "pointer", opacity: busy ? 0.7 : 1 }}>
            +{oz} oz
          </button>
        ))}
      </div>

      <SectionLabel>Today's log</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {todays.length === 0 && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLOR.inkSoft }}>Nothing logged yet today.</div>}
        {todays.map((w) => (
          <Card key={w.id} style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }} accent={COLOR.teal}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: COLOR.ink }}>{w.amount_oz} oz</span>
            <button onClick={() => onDelete(w.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: COLOR.danger, padding: isMobile ? 6 : 4 }}><Trash2 size={16} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}
